import React from "react";
import { Alert, Box, Paper, Stack, Typography, useTheme } from "@mui/material";

import AbbreviateNumber from "components/formatting/AbbreviateNumber";
import { PricingModelUsage } from "../tabs/PrePurchaseUsage";
import BillingUsageEmailList from "./BillingUsageEmailList";
import ISOPercentage, { toISOPercent } from "components/formatting/ISOPercentage";
import { BillingUsageEmail } from "../types/BillingUsageEmail";
import { useTranslation } from "react-i18next";

interface PrePurchaseUsageCardProps {
  emailNotifications: Array<BillingUsageEmail>;
  pricingModelUsage: PricingModelUsage;
}

export default function PrePurchaseUsageCard(props: PrePurchaseUsageCardProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const allotment = props.pricingModelUsage.pricingModel.allotment.count;
  const current = props.pricingModelUsage.usage;
  const usedPercent = (current / allotment);
  const fillPercent = Math.min(usedPercent, 1) * 100;
  const emptyPercent = (1 - Math.min(usedPercent, 1)) * 100;

  let color = theme.colors.success.main;
  if (fillPercent >= 90) {
    color = theme.colors.error.main;
  } else if (fillPercent >= 75) {
    color = theme.colors.warning.main;
  }

  const showUsageWarning = usedPercent > 1;
  const usageOverageAmount =  i18n.t("intlNumber", {
    val: current - allotment
  });
  const usageOveragePercent = toISOPercent(i18n, usedPercent - 1, {
    minimumSignificantDigits: 2,
    maximumSignificantDigits: 2
  });

  return (
    <Paper key={`pricingModelUsage.${props.pricingModelUsage.pricingModel.name}`}>
      <Stack
        sx={{
          gap: 1,
          p: 1
        }}>
        <Box>
          <Stack direction="row" sx={{
            justifyContent: "space-between"
          }}>
            <Typography variant="h4">{props.pricingModelUsage.pricingModel.name}</Typography>
            <Stack
              direction="row"
              sx={{
                alignItems: "end",
                gap: 0.5
              }}>
              <AbbreviateNumber value={current} hideTooltip={true} />
              <Typography>of</Typography>
              <AbbreviateNumber value={allotment} hideTooltip={true} />
              <span>{"("}<ISOPercentage value={usedPercent} hideTooltip={true} />{")"}</span>
            </Stack>
          </Stack>
          <Stack direction="row" sx={{
            height: 24
          }}>
            <Box
              sx={{
                width: `${fillPercent}%`,
                bgcolor: color
              }}></Box>
            <Box
              sx={{
                width: `${emptyPercent}%`,
                backgroundColor: theme.palette.grey[300]
              }}></Box>
          </Stack>
          <Typography>{props.pricingModelUsage.pricingModel.description}</Typography>
        </Box>
        {showUsageWarning &&
          <Alert component="div" severity="warning" variant="outlined">
            {i18n.t("pre-purchase-usage.usage-alert-warning", {amount: usageOverageAmount, percent: usageOveragePercent, unit: props.pricingModelUsage.pricingModel.per})}
          </Alert>
        }
        <Box>
          {props.emailNotifications.length > 0 &&
            <BillingUsageEmailList emailNotifications={props.emailNotifications} />
          }
        </Box>
      </Stack>
    </Paper>
  );
}